import { useEffect, useMemo, useState } from "react";
import { useSEO } from "@/hooks/useSEO";
import { Link } from "react-router-dom";
import { Search, Zap, Mail, Coins, Gift, ArrowRight } from "lucide-react";
import { MYSTERY_IMG } from "@/pages/MysteryBox";
import { api } from "@/lib/api";
import { GameCard } from "@/components/store/GameCard";
import { Skeleton } from "@/components/ui/skeleton";

const PAGE = 48;
const HERO = "https://images.unsplash.com/photo-1542751371-adc38448a05e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const Hero = ({ count }) => (
  <section className="grid grid-cols-1 md:grid-cols-12 gap-4">
    <div className="md:col-span-8 relative rounded-xl overflow-hidden border border-white/10 min-h-[260px] sm:min-h-[340px] rise">
      <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#08090C] via-[#08090C]/80 to-transparent" />
      <div className="relative p-6 sm:p-12 h-full flex flex-col justify-end">
        <p className="label-eyebrow text-emerald-400 mb-4">Steam · Rockstar · Xbox</p>
        <h1 className="font-display font-bold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-2xl">
          Game keys,<br />paid in <span className="text-emerald-400">USDT</span>.
        </h1>
        <p className="text-gray-300 mt-5 max-w-md text-sm sm:text-base">No account needed. Pick your games, send USDT on TRC20, ERC20, SOL or BEP20, and get your activation code by email.</p>
      </div>
    </div>
    <div className="md:col-span-4 grid grid-cols-[auto_1fr] md:grid-cols-1 gap-3 sm:gap-4">
      <div className="rounded-xl border border-white/10 bg-[#0F1117] p-4 sm:p-6 rise" style={{ animationDelay: "80ms" }}>
        <p className="label-eyebrow text-gray-500">In the vault</p>
        <p data-testid="hero-title-count" className="font-mono text-3xl sm:text-5xl font-bold mt-2 sm:mt-3">{count}</p>
        <p className="text-gray-400 text-xs sm:text-sm mt-1">titles ready to activate</p>
      </div>
      <div className="rounded-xl border border-white/10 bg-[#0F1117] p-4 sm:p-6 space-y-2.5 sm:space-y-4 rise" style={{ animationDelay: "160ms" }}>
        {[[Coins, "Pay in USDT on 4 networks"], [Zap, "Verified by our team quickly"], [Mail, "Code delivered to your inbox"]].map(([Icon, t]) => (
          <div key={t} className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm"><Icon size={16} className="text-emerald-400 shrink-0" />{t}</div>
        ))}
      </div>
    </div>
  </section>
);

const MysteryBanner = () => (
  <Link to="/mystery" data-testid="home-mystery-banner" className="group relative flex items-center gap-4 sm:gap-6 rounded-xl border border-emerald-500/30 bg-[#0F1117] overflow-hidden p-4 sm:p-8 hover:border-emerald-500/70 transition-colors rise">
    <img src={MYSTERY_IMG} alt="" className="w-20 h-20 sm:w-32 sm:h-32 rounded-lg object-cover shrink-0 transition-transform duration-500 group-hover:scale-105" />
    <div className="flex-1 min-w-0">
      <p className="label-eyebrow text-emerald-400 flex items-center gap-2"><Gift size={14} /> Mystery Box</p>
      <p className="font-display font-bold tracking-tight text-base sm:text-2xl mt-2">10 to 1000 random game codes. $2 per code.</p>
      <p className="text-gray-400 text-xs sm:text-sm mt-1">Every code is worth $5+. Some unlock $50–$100 games.</p>
    </div>
    <ArrowRight className="hidden sm:block text-emerald-400 transition-transform group-hover:translate-x-1" />
  </Link>
);

export default function Home() {
  useSEO({ path: "/" });
  const [games, setGames] = useState(null);
  const [q, setQ] = useState("");
  const [platform, setPlatform] = useState("All");
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => setLimit(PAGE), [q, platform]);

  useEffect(() => { api.get("/games").then((r) => setGames(r.data)); }, []);

  const shown = useMemo(() => (games || []).filter((g) =>
    (platform === "All" || g.platform === platform) && g.name.toLowerCase().includes(q.toLowerCase())), [games, q, platform]);
  const filters = useMemo(() => ["All", ...new Set(["Steam", "Rockstar", "Xbox", ...(games || []).map((g) => g.platform)])], [games]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-5 sm:pt-8 space-y-10 sm:space-y-14">
      <Hero count={games ? games.length : "—"} />
      <MysteryBanner />
      <section id="catalog" className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4 justify-between">
          <div>
            <p className="label-eyebrow text-gray-500">Catalog</p>
            <h2 className="font-display font-semibold tracking-tight text-lg mt-2">Browse all keys</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 min-w-0">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                data-testid="search-game-input"
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search games..."
                className="h-11 sm:h-10 w-full sm:w-72 pl-9 pr-3 rounded-md bg-[#0F1117] border border-white/10 text-sm focus:outline-none focus:border-emerald-500/60"
              />
            </div>
            <div data-testid="platform-filters" className="flex flex-nowrap sm:flex-wrap overflow-x-auto no-scrollbar gap-1 p-1 rounded-md bg-[#0F1117] border border-white/10">
              {filters.map((f) => (
                <button
                  key={f}
                  data-testid={`platform-filter-${f.toLowerCase().replace(/[^a-z]/g, "")}`}
                  onClick={() => setPlatform(f)}
                  className={`shrink-0 whitespace-nowrap px-3 h-10 sm:h-8 rounded text-sm font-medium transition-colors ${platform === f ? "bg-emerald-500 text-[#08090C]" : "text-gray-400 hover:text-white"}`}
                >{f}</button>
              ))}
            </div>
          </div>
        </div>
        {!games ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-72 bg-white/5" />)}
          </div>
        ) : shown.length === 0 ? (
          <p data-testid="catalog-empty-state" className="text-gray-400 py-16 text-center">{q ? "No games match your search." : `No ${platform} titles in stock yet.`}</p>
        ) : (
          <div data-testid="catalog-grid" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {shown.slice(0, limit).map((g, i) => <GameCard key={g.id} game={g} index={i % PAGE} />)}
          </div>
        )}
        {shown.length > limit && (
          <div className="text-center">
            <button data-testid="catalog-show-more" onClick={() => setLimit(limit + PAGE)} className="h-11 px-6 rounded-md border border-white/15 font-semibold hover:border-emerald-500/60 transition-colors">
              Show more <span className="font-mono text-gray-500 ml-1">({shown.length - limit} left)</span>
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
